import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';

import { useDeleteNetworkMutation, useGetNetworksQuery } from 'api/main';
import { ORDER_BY, RoleNames, listingActionButtonStyle, sortDirection } from 'utils/constants';
import { parseFullName } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    ActionsMenu,
    ConfirmDialog,
    EmptyTableBody,
    EnhancedTableHead,
    ListContainer,
} from 'components';
import { MenuDelete, MenuEdit } from 'components/ActionsMenu/actions';
import {
    Box,
    Typography,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';
import { useMatchAtLeastOneRole } from 'utils/hooks';

const DeleteNetworkItem = React.forwardRef((props, ref) => <MenuDelete ref={ref} {...props} />);
const EditNetworkItem = React.forwardRef((props, ref) => <MenuEdit ref={ref} {...props} />);

const tableHeads = [
    {
        id: ORDER_BY.networkName,
        width: 20,
        isSortable: true,
        label: texts.NETWORK_NAME,
        align: 'center',
    },
    {
        id: ORDER_BY.fundName,
        width: 30,
        isSortable: false,
        label: texts.PRODUITS,
    },
    {
        id: 'network-admin',
        width: 45,
        isSortable: false,
        label: texts.ANIMATORS,
    },
    {
        id: 'actions',
        width: 5,
        isSortable: true,
        label: '',
    },
];

const NetworksTable = () => {
    const dispatch = useDispatch();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [dialogNetwork, setDialogNetwork] = useState(null);
    const [filters, setFilters] = useState({
        orderBy: ORDER_BY.networkName,
        direction: sortDirection.ASC,
    });

    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);

    const { data: networks = [], isFetching: listLoader } = useGetNetworksQuery(filters);
    const [deleteNetwork, { isLoading: deleteLoader, isSuccess: deleteSuccess }] =
        useDeleteNetworkMutation();

    const filteredTableHeads = useMemo(
        () => tableHeads.filter(th => (userIsStandardServicer ? th.id !== 'actions' : true)),
        [userIsStandardServicer]
    );

    const handleOpenDeleteDialog = useCallback(
        id => () => {
            setDeleteOpen(true);
            setDialogNetwork(networks.find(n => n.id === id));
        },
        [networks]
    );

    const handleEditNetwork = useCallback(
        id => () => dispatch(push(`/networks/edit/${id}`)),
        [dispatch]
    );

    const renderRow = useCallback(
        network => (
            <TableRow key={network.id}>
                <TableCell>{network.name}</TableCell>
                <TableCell>
                    {network.investmentFunds.map(p => (
                        <Typography key={p.id}>{p.name}</Typography>
                    ))}
                </TableCell>
                <TableCell>
                    {network.networkAdmins.map(a => (
                        <Typography key={a.id}>{parseFullName(a)}</Typography>
                    ))}
                </TableCell>
                {!userIsStandardServicer && (
                    <TableCell>
                        <ActionsMenu>
                            <EditNetworkItem onClick={handleEditNetwork(network.id)} />
                            <DeleteNetworkItem onClick={handleOpenDeleteDialog(network.id)} />
                        </ActionsMenu>
                    </TableCell>
                )}
            </TableRow>
        ),
        [handleEditNetwork, handleOpenDeleteDialog, userIsStandardServicer]
    );

    const handleSortNetworks = useCallback((property, direction) => {
        setFilters(oldValue => ({ ...oldValue, orderBy: property, direction }));
    }, []);

    const handleDeleteNetwork = useCallback(() => {
        deleteNetwork(dialogNetwork.id);
    }, [deleteNetwork, dialogNetwork]);

    useEffect(() => {
        if (deleteSuccess) {
            setDeleteOpen(false);
            setDialogNetwork(null);
        }
    }, [deleteSuccess]);

    return (
        <ListContainer>
            <Typography variant="h5" align="left" gutterBottom>
                {texts.NETWORKS}
            </Typography>
            {!userIsStandardServicer && (
                <Box marginY={2} textAlign="right">
                    <ActionButton
                        variant="outlined"
                        sx={listingActionButtonStyle}
                        onClick={() => dispatch(push('/networks/create'))}
                    >
                        {texts.CREATE_NETWORK}
                    </ActionButton>
                </Box>
            )}
            <ConfirmDialog
                open={deleteOpen}
                title={texts.SUPPRIMER}
                loading={deleteLoader}
                handleClose={() => setDeleteOpen(false)}
                handleConfirm={handleDeleteNetwork}
            >
                {texts.ETES_VOUS_SURS_DE_VOULOIR_SUPPRIMER}
            </ConfirmDialog>
            <TableContainer>
                <Table style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {filteredTableHeads.map(c => (
                            <col key={c.id} width={`${c.width}%`} />
                        ))}
                    </colgroup>
                    <EnhancedTableHead
                        headCells={filteredTableHeads}
                        orderBy={filters.orderBy}
                        direction={filters.direction}
                        handleSort={handleSortNetworks}
                    />
                    {networks.length === 0 ? (
                        <EmptyTableBody
                            colSpan={tableHeads.length}
                            loading={listLoader}
                            message={texts.NO_DATA_AVAILABLE}
                        />
                    ) : (
                        <TableBody>{networks.map(renderRow)}</TableBody>
                    )}
                </Table>
            </TableContainer>
        </ListContainer>
    );
};

export default NetworksTable;
